// core/notification-class.mjs — which notification rows are which CLASS. ONE statement.
//
// ⚠️ THIS FILE HAS A TWIN: `server-new-libs/lib/notification_class.mjs`. Same content,
// different filename (hyphen here, underscore there — the same arrangement as
// match-score.mjs / match_score.mjs and binder-rules.mjs / binder_rules.mjs). It imports
// nothing, so the pair really is byte-identical; keep it that way.
//
// WHY IT EXISTS. The rule was written twice on 2026-08-06 — `classify()` in
// NotificationsPanel.jsx decided how a row RENDERS, and `KIND_CLASS` in
// lib/core_notifications.js decided whether the server would let it be DISMISSED. Two
// hand-maintained statements of one rule, with nothing detecting drift. That is exactly the
// disease B261 fixed for search filters and B273 for district counts, reintroduced within
// hours of a migration comment saying "one definition". The failure it invites is quiet: a
// row that shows a ✕ the server then 409s, or worse, one the server lets an agent dismiss
// when it is owed to a person.
//
// THE MODEL — three kinds of DEBT, not three kinds of event (designer, round 1):
//   A  owed to a PERSON        → badges. May be marked read; may NEVER be dismissed,
//                                because resolving the underlying thing is the only exit.
//   B  owed to your JUDGEMENT  → never badges. Dismissible: ignoring a hint is an answer.
//   C  not a debt at all       → a count, never rows. No read state; never written down.
//
// ⚠️ `price` and `ended` are listed as B and are CONDITIONAL — see CONDITIONAL_PREFIXES.
// A price move is a row only when it lands on a property the viewer is actually working
// (measured 2026-08-06: 74 of 2,234 in 7 days); otherwise it is one of 157 in a counter.
// The server cannot see that condition — it has no idea what the viewer is working — so it
// treats the prefix by its DISMISSIBLE form. That is safe in the only direction that
// matters: a C row carries no dismiss control, so no key for it is ever written.

export const ROW_CLASS = Object.freeze({
  // ── A — someone is waiting on you ──
  ask: 'A',        // a colleague blocked on my access decision
  notif: 'A',      // a draft that failed or is still pending
  nudge: 'A',      // a meeting owing an outcome (critical / high)
  meeting: 'A',    // a client asked to view a property
  alert: 'A',      // AI or background-worker failure
  // A deposit figure REWRITTEN after it was first recorded (B341 — anyone on the meeting
  // can rewrite the figures). Class A, and the two designs agreed independently: that row
  // shipped "NOT capped, and if this list is ever long enough to need a cap that IS the
  // finding", which is zone A's rule stated from the other direction. Nobody is literally
  // waiting, but it is an integrity alert about MONEY with no other surface to land on —
  // the same reasoning that puts `alert` here.
  binder: 'A',
  // A meeting starting within the configured window (0451). Class A: the client is
  // waiting, imminently. It has no downstream resolution — nothing ever reports 'I have
  // read this' — so like the other signal-only A rows it is cleared by an explicit
  // per-row read rather than by a state change somewhere else.
  upcoming: 'A',
  // The flat went to another client and this agent's visit was auto-cancelled (0690). Class
  // A: the agent owes their CLIENT a call. Nothing downstream resolves it, so like
  // `upcoming` it is cleared by an explicit per-row read.
  gone: 'A',
  // A scheduled share that failed or was missed (share_plans.js, 2026-10-09): the agent must log
  // in, pay or reschedule — nothing else will. Cleared by an explicit per-row read.
  sharefail: 'A',
  // A WhatsApp client on the agent's own line has waited 30+ minutes for an answer (core 4226, owner 2026-11-04
  // "yes build E": 77 client questions went unanswered in a week). The reply is the only exit - the row leaves
  // the feed by itself once the agent writes back in that chat.
  waiting: 'A',
  // ── B — a hint. Nobody can call it in ──
  engaged: 'B',    // a client read the listing closely
  reach: 'B',      // a client tapped Call / WhatsApp
  access: 'B',     // my own access request came back — the wait is over
  quiet: 'B',      // a client went quiet
  nudgemed: 'B',   // medium nudge — feed-only, never badged
  price: 'B',      // conditional (see below)
  ended: 'B',      // conditional (see below)
  shareok: 'B',    // a scheduled share went live — good news, nobody has to act
})

// Prefixes whose class depends on data only the CLIENT holds (is the viewer working this
// property?). They render as C when they are not, and C rows have no controls, so the
// server never receives a key for one.
export const CONDITIONAL_PREFIXES = Object.freeze(new Set(['price', 'ended']))

// A key is `<prefix>:<id...>`. The tail may contain colons — synthetic ids do (an
// engagement row is `engaged:<share_id>:<session_start_ms>`), so the prefix is only ever
// the text before the FIRST colon.
export const ROW_KEY_RE = /^([a-z]+):[A-Za-z0-9:_.\-]{1,180}$/

export function prefixOfKey(rowKey) {
  const m = ROW_KEY_RE.exec(rowKey || '')
  return m ? m[1] : null
}

// null for an unknown or malformed key — callers must REFUSE rather than guess. A future
// row type must not become silently dismissible by being unrecognised.
export function classOfKey(rowKey) {
  const p = prefixOfKey(rowKey)
  return p ? (ROW_CLASS[p] || null) : null
}

// The one question the server actually asks. Deliberately phrased as a refusal: an unknown
// key is not dismissible either.
export function mayDismiss(rowKey) {
  return classOfKey(rowKey) === 'B'
}

// ── Feed row → prefix ────────────────────────────────────────────────────────
// ⚠️ THIS LIVES HERE, NOT IN THE PANEL, AND THE REASON IS A TEST THAT KEPT BREAKING.
// The drift detector used to read this function out of NotificationsPanel.jsx with a
// regex, because a .jsx file cannot be imported by `node --test`. That regex broke twice
// in one day: once when the nudge branch became a nested ternary (a prefix stopped looking
// reachable) and once when widening it swept up a COMPARISON literal as if it were a
// prefix. A scraper that must be re-tuned every time the code it reads is edited is not a
// detector, it is a second thing to maintain.
//
// So the mapping moved to the module that owns the vocabulary — which is where it always
// belonged: a prefix IS the class vocabulary, and this function is the only place a feed
// row acquires one. The test now calls it with sample rows instead of reading its source,
// and the panel imports it.
//
// It knows nothing about rendering, React, or i18n — it takes the feed row's plain shape
// and returns a word, so it stays pure and stays here.
export function rowPrefix(row) {
  switch (row?.kind) {
    case 'ask': return 'ask'
    case 'notif': return 'notif'
    case 'access': return 'access'
    case 'ai_alert': case 'system_alert': return 'alert'
    case 'binder_change': return 'binder'
    case 'price': return 'price'
    case 'ended': return 'ended'
    case 'share_run': return row.r?.state === 'published' ? 'shareok' : 'sharefail'
    case 'client_waiting': return 'waiting'
    // Medium is feed-only and never badges, so it is a hint rather than an obligation.
    // 'upcoming' (0451) is its own prefix rather than a nudge tier: critical/high badge
    // unconditionally because doing the work ends them, and a meeting that has not
    // happened yet has no such ending — it is cleared by being read.
    case 'nudge': {
      const tier = row.n?.tier
      if (tier === 'medium') return 'nudgemed'
      if (tier === 'upcoming') return 'upcoming'
      if (tier === 'gone') return 'gone'
      return 'nudge'
    }
    case 'share': {
      const k = row.s?.kind
      if (k === 'meeting_requested') return 'meeting'
      // A tap on Call/WhatsApp is a client REACHING for you about one flat — not an
      // obligation (they acted, nobody is blocked), but far too pointed to fold into a
      // counter. 6 in 14 days, so it stays affordable as a row.
      if (k === 'call_clicked' || k === 'whatsapp_clicked') return 'reach'
      // The client who is interested but has not said so. Class B, NOT A: nobody is
      // waiting — and if the parked-tab guard ever slips, a wrong HINT degrades while a
      // wrong OBLIGATION would spend the badge's credibility.
      if (k === 'engaged') return 'engaged'
      return null          // 'opened' has no standalone row — it folds into the stream
    }
    default: return null
  }
}
